import { router } from '@inertiajs/react'
import DashboardLayout from '@/components/DashboardLayout'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { AlertTriangle, Wallet } from 'lucide-react'

interface Entry {
  id: number
  beneficiary: { id: number; name?: string }
  label: string
  source_date: string
  base_amount: number
  rate: number
  amount: number
  status: string
  settled_at?: string | null
  reversed_at?: string | null
  reversal_reason?: string | null
}

interface SummaryRow {
  id: number
  name: string
  count: number
  total: number
  pending: number
  settled: number
}

interface Props {
  period: { from: string; to: string }
  beneficiaries: {
    id: number
    name: string
    commission_enabled: boolean
    commission_percentage: number
  }[]
  filters: { beneficiary_id?: string }
  summary: SummaryRow[]
  entries: Entry[]
  reversals_after_settlement: Entry[]
  totals: { earned: number; settled: number; pending: number; reversed: number }
}

const brl = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    v,
  )

const dia = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('pt-BR')

export default function CommissionsIndex({
  period,
  filters,
  summary,
  entries,
  reversals_after_settlement,
  totals,
}: Props) {
  const visit = (params: Record<string, unknown>) =>
    router.get(
      '/commissions',
      { ...period, ...filters, ...params },
      { preserveState: true },
    )

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div>
          <h2 className="text-2xl font-bold">Comissões</h2>
          <p className="text-sm text-muted-foreground">
            O que cada gerente ganhou nas vendas que registrou
          </p>
        </div>

        <Card>
          <CardContent className="p-6 flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label htmlFor="from">De</Label>
              <Input
                id="from"
                type="date"
                className="w-[170px]"
                defaultValue={period.from}
                onChange={(e) => visit({ from: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="to">Até</Label>
              <Input
                id="to"
                type="date"
                className="w-[170px]"
                defaultValue={period.to}
                onChange={(e) => visit({ to: e.target.value })}
              />
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4 sm:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>A pagar</CardDescription>
              <CardTitle className="text-2xl">{brl(totals.pending)}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Já pago</CardDescription>
              <CardTitle className="text-2xl">{brl(totals.settled)}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Estornado no período</CardDescription>
              <CardTitle className="text-2xl">{brl(totals.reversed)}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Estorno que caiu depois do pagamento: o dinheiro já saiu, então vira desconto
            no fechamento seguinte. Sem este bloco ele sumiria da conta em silêncio. */}
        {reversals_after_settlement.length > 0 && (
          <Card className="border-orange-300">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-600" />
                Estornos depois da liquidação
              </CardTitle>
              <CardDescription>
                Estas comissões já haviam sido pagas quando a venda foi
                estornada. Desconte no próximo fechamento.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1 text-sm">
                {reversals_after_settlement.map((entry) => (
                  <li key={entry.id} className="flex justify-between gap-4">
                    <span>
                      {entry.beneficiary.name} · {entry.label}
                    </span>
                    <span className="font-medium text-orange-700">
                      −{brl(entry.amount)}
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Por gerente</CardTitle>
          </CardHeader>
          <CardContent>
            {summary.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-10 text-center text-muted-foreground">
                <Wallet className="h-10 w-10 opacity-40" />
                <p className="text-sm">
                  Nenhuma comissão apurada neste período.
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Gerente</TableHead>
                    <TableHead className="text-right">Vendas</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">A pagar</TableHead>
                    <TableHead className="text-right">Ação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">{row.name}</TableCell>
                      <TableCell className="text-right">{row.count}</TableCell>
                      <TableCell className="text-right">
                        {brl(row.total)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {brl(row.pending)}
                      </TableCell>
                      <TableCell className="text-right">
                        {row.pending > 0 && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm">Marcar como pago</Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  Pagar {brl(row.pending)} para {row.name}?
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  Isso lança uma despesa de{' '}
                                  <strong>{brl(row.pending)}</strong> na
                                  categoria Comissões, no caixa de hoje, e marca
                                  as comissões do período como pagas.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() =>
                                    router.post('/commissions/mark_as_paid', {
                                      beneficiary_id: row.id,
                                      from: period.from,
                                      to: period.to,
                                    })
                                  }
                                >
                                  Confirmar pagamento
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {entries.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Detalhe</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Venda</TableHead>
                    <TableHead>Gerente</TableHead>
                    <TableHead className="text-right">Valor da venda</TableHead>
                    {/* A taxa que valia quando a venda foi feita, não a de hoje. */}
                    <TableHead className="text-right">% na venda</TableHead>
                    <TableHead className="text-right">Comissão</TableHead>
                    <TableHead>Situação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>
                        <div className="font-medium">{entry.label}</div>
                        <div className="text-xs text-muted-foreground">
                          {dia(entry.source_date)}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {entry.beneficiary.name}
                      </TableCell>
                      <TableCell className="text-right">
                        {brl(entry.base_amount)}
                      </TableCell>
                      <TableCell className="text-right">
                        {entry.rate}%
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {brl(entry.amount)}
                      </TableCell>
                      <TableCell>
                        {entry.settled_at ? (
                          <Badge variant="secondary">Paga</Badge>
                        ) : (
                          <Badge variant="outline">A pagar</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  )
}
